import { createContext, useContext, useReducer, useCallback, useRef, useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  scanReducer,
  initialState,
  SSE_EVENT_TYPES,
  type ScanEventState,
} from "@/hooks/useScanEvents";
import { trpc } from "@/lib/trpc";

interface ScanContextValue {
  state: ScanEventState;
  connect: (scanId: string) => void;
  disconnect: () => void;
  showPanel: boolean;
  setShowPanel: (show: boolean) => void;
}

const ScanContext = createContext<ScanContextValue | null>(null);

export function ScanProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(scanReducer, initialState);
  const eventSourceRef = useRef<EventSource | null>(null);
  const [showPanel, setShowPanel] = useState(false);

  const connect = useCallback((scanId: string) => {
    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    dispatch({ type: "reset" });

    const es = new EventSource(`/api/scan-events/${scanId}`);
    eventSourceRef.current = es;

    es.addEventListener("connected", () => {
      dispatch({ type: "connected" });
    });

    for (const type of SSE_EVENT_TYPES) {
      es.addEventListener(type, (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          dispatch({ type: "event", event: data });
        } catch {
          console.warn(`[ScanContext] Failed to parse ${type} event`);
        }
      });
    }

    es.onerror = () => {
      dispatch({ type: "disconnected" });
    };
  }, []);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    dispatch({ type: "disconnected" });
  }, []);

  // Auto-reconnect to active scan on mount
  const { data: activeScans } = trpc.scans.getActive.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  const hasAutoConnected = useRef(false);
  useEffect(() => {
    if (hasAutoConnected.current) return;
    if (!activeScans || activeScans.length === 0) return;
    // Don't auto-connect if we already have an active connection
    if (eventSourceRef.current) return;

    hasAutoConnected.current = true;
    connect(activeScans[0]!.scanId);
  }, [activeScans, connect]);

  // Cleanup on unmount (dashboard layout unmounting = leaving dashboard entirely)
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  return (
    <ScanContext.Provider value={{ state, connect, disconnect, showPanel, setShowPanel }}>
      {children}
    </ScanContext.Provider>
  );
}

export function useScan(): ScanContextValue {
  const ctx = useContext(ScanContext);
  if (!ctx) {
    throw new Error("useScan must be used within a <ScanProvider>");
  }
  return ctx;
}
